import React from 'react';

import { AccordionBody } from './AccordionBody/AccordionBody';
import { AccordionHeader } from './AccordionHeader/AccordionHeader';
import type { AccordionItemId, OpenSections } from './models';

export interface IAccordionState {
  openSections: OpenSections;
}

export interface IAccordionProps {
  children: (onToggled: (id: AccordionItemId) => void, openSections: OpenSections) => React.ReactNode;
  allowMultipleOpen?: boolean;
  className?: string;
  alwaysHaveOneItemOpen?: boolean;
  preventClickHandler?: boolean;
}

export class Accordion extends React.PureComponent<IAccordionProps, IAccordionState> {
  static Header = AccordionHeader;
  static Body = AccordionBody;

  static defaultProps = {
    allowMultipleOpen: false,
  };

  state: IAccordionState = { openSections: {} };

  render() {
    const {
      props: { children, className },
      state: { openSections },
    } = this;

    return (
      <div className={className || ''} onClick={this.handleClick}>
        {children(this.onToggled, openSections)}
      </div>
    );
  }

  private onToggled = (index: AccordionItemId, isClickedToggle = false) => {
    const {
      props: { allowMultipleOpen, preventClickHandler = false, alwaysHaveOneItemOpen = false },
      state: { openSections },
    } = this;

    // if user clicks on toggle and preventClickHandler is true, do not update state
    if (isClickedToggle && preventClickHandler) return;

    const isOpen = !!openSections[index];

    if (!allowMultipleOpen) {
      if (alwaysHaveOneItemOpen && isOpen) return;

      return this.setState({
        openSections: {
          [index]: !isOpen,
        },
      });
    }

    // if allowMultipleOpen is true and alwaysHaveOneItemOpen is true,
    // only allow to close accordion item when there is more than one items open in the accordion
    if (alwaysHaveOneItemOpen && Object.values(openSections).filter(Boolean).length === 1 && isOpen) return;

    this.setState(prevState => ({
      openSections: {
        ...prevState.openSections,
        [index]: !isOpen,
      },
    }));
  };

  // Click event bubbles up from child to parent. Handles the onClick on any of the child anchor tags.
  // When anchor tag is clicked, close the accordion
  private handleClick: React.MouseEventHandler<HTMLDivElement> = e => {
    if (e.target instanceof HTMLDivElement && e.target.localName === 'a') {
      this.setState({ openSections: {} });
    }
  };
}
